import React, { ForwardedRef, ReactNode } from 'react';
import clsx from 'clsx';
import styles from '@danske/sapphire-css/components/table/table.module.css';
import { DOMAttributes } from '@react-types/shared';
import { useHover } from '@react-aria/interactions';
import { useFocusRing } from '@react-aria/focus';
import { mergeProps, useId } from '@react-aria/utils';
import { TableRowContext } from './TableRowContext';
import { useTableContainerContext } from './TableContainerContext';
import { SapphireStyleProps, useSapphireStyleProps } from '../../utils';

export interface TableRowProps
  extends Omit<
      DOMAttributes<HTMLTableRowElement>,
      'className' | 'style' | 'dangerouslySetInnerHTML'
    >,
    SapphireStyleProps {
  /**
   * Note: Currently, we don't officially support non-table elements.
   *
   * @default 'tr'
   */
  elementType?: 'tr';

  children?: React.ReactNode;

  /**
   * Content, rendered after the row element.
   * Used to show some details for each row, which is not included in the columns.
   */
  expandedView?: ReactNode;

  isDisabled?: boolean;

  /** Used for the pressed state, relevant for  interactive rows */
  isActive?: boolean;

  /** Marks the row as selected */
  isSelected?: boolean;

  /** Marks the row as highlighted */
  isHighlighted?: boolean;

  /**
   * Marks the row as interactive. Should be `true` when the row has an action
   * that can be triggered by clicking the row.
   * when the {@Link Table.Container} has a prop isInteractive, the value is overridden
   */
  isInteractive?: boolean;
  /**
   * The number of columns in the row
   */
  columnCount?: number;
}

export function sapphireRowClasses({
  isHovered,
  isFocusVisible,
  isPressed,
  isSelected,
  isHighlighted,
  isInteractive,
  isExpanded,
}: {
  isHovered?: boolean;
  isFocusVisible?: boolean;
  isPressed?: boolean;
  isSelected?: boolean;
  isHighlighted?: boolean;
  isInteractive?: boolean;
  isExpanded?: boolean;
}): string {
  return clsx(
    styles['sapphire-table__row'],
    styles['js-hover'],
    styles['js-focus'],

    {
      [styles['is-hover']]: isHovered,
      [styles['is-focus']]: isFocusVisible,
      [styles['sapphire-table__row--active']]: isPressed,
      [styles['sapphire-table__row--selected']]: isSelected,
      [styles['sapphire-table__row--highlighted']]: isHighlighted,
      [styles['sapphire-table__row--interactive']]: isInteractive,
      [styles['sapphire-table__row--expanded']]: isExpanded,
    }
  );
}

/**
 * Corresponding to `tr` element in native html. Should be rendered inside
 * {@Link Table.Body}
 */

export const TableRow = React.forwardRef(function TableRow(
  {
    children,
    elementType,
    isDisabled,
    isActive: isPressed,
    isSelected,
    isHighlighted,
    isInteractive,
    columnCount,
    expandedView,
    ...props
  }: TableRowProps,
  ref: ForwardedRef<HTMLTableRowElement>
) {
  const { styleProps, filteredProps } = useSapphireStyleProps(props);
  const { hoverProps, isHovered } = useHover({
    // Avoid unnecessary listeners if rows are not interactive
    isDisabled,
  });
  const { focusProps, isFocusVisible } = useFocusRing({});

  const ElementType = elementType || 'tr';
  const role = props.role || ElementType === 'tr' ? undefined : 'row';
  const expandedViewId = useId();

  const context = useTableContainerContext();

  return (
    <TableRowContext.Provider value={{ expandedViewId }}>
      <ElementType
        ref={ref}
        {...mergeProps(filteredProps, hoverProps, focusProps, { role })}
        className={clsx(
          sapphireRowClasses({
            isHovered,
            isFocusVisible,
            isPressed,
            isSelected,
            isHighlighted,
            isExpanded: !!expandedView,
            isInteractive: context?.isInteractive || isInteractive,
          }),
          styleProps.className
        )}
        style={styleProps.style}
      >
        {children}
      </ElementType>
      {expandedView ? (
        <ElementType
          id={expandedViewId}
          className={clsx(styles['sapphire-table__row-expanded-view'])}
        >
          {ElementType === 'tr' ? (
            <td colSpan={columnCount}>{expandedView}</td>
          ) : (
            <div>{expandedView}</div>
          )}
        </ElementType>
      ) : null}
    </TableRowContext.Provider>
  );
});
